import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createElementBlock as _createElementBlock, unref as _unref, Fragment as _Fragment } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "rounded-lg justify-space-between mb-4 pa-2 d-flex" }
const _hoisted_3 = { class: "d-flex align-center" }
const _hoisted_4 = { class: "text-white text-subtitle-2 font-weight-bold" }
const _hoisted_5 = { class: "font-weight-medium" }
const _hoisted_6 = { class: "text-caption text-medium-emphasis" }
const _hoisted_7 = { class: "d-flex gap-1" }
const _hoisted_8 = { class: "text-center pt-2" }
const _hoisted_9 = {
  class: "sticky-header bg-grey-lighten-4 d-flex justify-space-between align-center px-4 py-3",
  style: {"border-bottom":"1px solid #e0e0e0"}
}
const _hoisted_10 = { class: "text-h6 font-weight-medium" }
const _hoisted_11 = { class: "pa-6" }

import { User } from './models/user'
import { ref, onMounted, computed, watch } from 'vue'

import { useUserStore } from './store'

import {
  validateEmail,
  validatePasswords,
  generatePassword
} from './helpers'

// ─── STORES ────────────────────────────────────────────────────────────────
const itemsPerPage = 10

export default /*@__PURE__*/_defineComponent({
  __name: 'TeamsView',
  setup(__props) {


// ─── IMPORTS ────────────────────────────────────────────────────────────────
const userStore = useUserStore()

// ─── REFS: UI Flags and Data ───────────────────────────────────────────────
const loading = ref(false)
const activeEditUser = ref(false)
const activeCreateNewUser = ref(false)
const loadingCreateUser = ref(false)
const loadingEditUser = ref(false)
const searchUsers = ref('')
const page = ref(1)
const showPassword = ref(false)
const formTouched = ref(false)
const formRef = ref()
const formValid = ref(false)
const headers = [
  { title: 'NOMBRE', key: 'name' },
  { title: 'EMAIL', key: 'email' },
  { title: 'ROL', key: 'role' },
  { title: 'ESTADO', key: 'status' },
  { title: 'ACCIONES', key: 'actions' },
]

// ─── REFS: Usuario ──────────────────────────────────────────────────────
const users = ref<User[]>([])
const user = ref<User>(new User())
const password = ref('')
const confirmPassword = ref('')
const generateRandomPassword = ref(false)
const sendEmailPassword = ref(true)

// ─── ALERTAS ───────────────────────────────────────────────────────────────
const activeAlert = ref(false)
const textAlert = ref('')
const colorAlert = ref('error')

// ─── VALIDACIONES ──────────────────────────────────────────────────────────
const emailError = ref('')
const passwordError = ref('')

// ─── OPCIONES DE ROL ────────────────────────────────────────────────────────
const roleOptions = [
  { name: 'Administrador', key: 'admin' },
  { name: 'Supervisor', key: 'supervisor' }
]


// ─── COMPUTED ──────────────────────────────────────────────────────────────
const pageCount = computed(() => Math.ceil(users.value.length / itemsPerPage))

const getStatusLabel = (role: string) => {
  switch (role) {
    case 'admin':
      return 'Administrador'
    case 'supervisor':
      return 'Supervisor'
    case 'inactive':
      return 'Inactivo'
    default:
      return 'Desconocido'
  }
}

const getStatusColor = (role: string) => {
  switch (role) {
    case 'admin':
      return 'green'
    case 'supervisor':
      return 'blue'
    case 'inactive':
      return 'red'
    default:
      return 'grey'
  }
}

// ─── FUNCIONES AUXILIARES ──────────────────────────────────────────────────

// watcher para el checkbox de generar contraseña
watch(generateRandomPassword, (newVal) => {
  if (newVal) {
    const pwd = generatePassword()
    password.value = pwd
    confirmPassword.value = pwd
  } else {
    password.value = ''
    confirmPassword.value = ''
  }
})

// watcher para auto-generar folderName
watch([() => user.value.firstName, () => user.value.lastName], ([firstName, lastName]) => {
  if (firstName && lastName && !user.value.folderName) {
    const folderName = `${firstName.toLowerCase()}_${lastName.toLowerCase()}`
      .replace(/[^a-z0-9_]/gi, '') // eliminar caracteres especiales
      .replace(/\s+/g, '_') // reemplazar espacios con guiones bajos
    user.value.folderName = folderName
  }
})

const resetForm = () => {
  user.value = new User()
  password.value = ''
  confirmPassword.value = ''
  generateRandomPassword.value = false
  sendEmailPassword.value = true
  emailError.value = ''
  passwordError.value = ''
  formTouched.value = false
}

// ─── EVENTOS / BOTONES ─────────────────────────────────────────────────────


const getUsers = async () => {
  loading.value = true
  try {
    users.value = await userStore.getEmployees()
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al cargar los colaboradores. Por favor, intente nuevamente.'
    colorAlert.value = 'error'
  } finally {
    loading.value = false
  }
}

const btnSetActiveCreateUser = () => {
  activeCreateNewUser.value = !activeCreateNewUser.value
  resetForm()
}

const btnSetActiveEditUser = async (selectedUser: User) => {
  user.value = new User(selectedUser)
  // Limpiar campos de contraseña en modo edición
  password.value = ''
  confirmPassword.value = ''
  generateRandomPassword.value = false
  passwordError.value = ''
  emailError.value = ''
  activeEditUser.value = true
}

const createUser = async () => {
  await userStore.postUser({
    email: user.value.email,
    password: password.value,
    name: user.value.name,
    user: user.value.toPlainObject(),
    sendEmailPassword: sendEmailPassword.value
  })
}

const validateForm = (): boolean => {
  let isValid = true

  // Validación de campos obligatorios
  if (!user.value.firstName || !user.value.lastName || !user.value.email || !user.value.role || !user.value.folderName) {
    activeAlert.value = true
    textAlert.value = 'Todos los campos obligatorios son requeridos'
    colorAlert.value = 'error'
    isValid = false
  }

  // Validación de email
  if (!user.value.email) {
    emailError.value = 'Email requerido'
    isValid = false
  } else if (!validateEmail(user.value.email)) {
    emailError.value = 'Email no válido'
    isValid = false
  }

  // Validación de contraseñas
  const pwdValidation = validatePasswords(password.value, confirmPassword.value, generateRandomPassword.value)
  if (pwdValidation) {
    passwordError.value = pwdValidation
    isValid = false
    activeAlert.value = true
    textAlert.value = pwdValidation
    colorAlert.value = 'error'
  }

  return isValid
}

const copyToClipboard = () => {
  if (navigator?.clipboard) {
    navigator.clipboard.writeText(password.value)
      .then(() => {
        activeAlert.value = true
        textAlert.value = 'Contraseña copiada al portapapeles'
        colorAlert.value = 'success'
      })
      .catch(() => {
        activeAlert.value = true
        textAlert.value = 'Error al copiar la contraseña'
        colorAlert.value = 'error'
      });
  } else {
    activeAlert.value = true
    textAlert.value = 'Función de copiar no disponible'
    colorAlert.value = 'error'
  }
}

const btnCreateUser = async () => {
  formTouched.value = true

  const valid = await formRef.value?.validate()
  if (!valid.valid || !validateForm()) return

  user.value.name = user.value.firstName + ' ' + user.value.lastName
  user.value.type = 'teams'
  user.value.active = true

  loadingCreateUser.value = true
  try {
    await createUser()
    activeAlert.value = true
    textAlert.value = 'Colaborador creado exitosamente'
    colorAlert.value = 'success'
    activeCreateNewUser.value = false
    resetForm()
    await getUsers()
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al crear el colaborador. Por favor, intente nuevamente.'
    colorAlert.value = 'error'
  } finally {
    loadingCreateUser.value = false
  }
}

const btnUpdateUser = async () => {
  user.value.name = user.value.firstName + ' ' + user.value.lastName
  user.value.type = 'teams'
  user.value.active = true

  loadingEditUser.value = true
  try {
    await userStore.putUser(user.value)
    activeAlert.value = true
    textAlert.value = 'Colaborador actualizado exitosamente'
    colorAlert.value = 'success'
    activeEditUser.value = false
    await getUsers()
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al actualizar el colaborador. Por favor, intente nuevamente.'
    colorAlert.value = 'error'
  } finally {
    loadingEditUser.value = false
  }
}

const btnSendPasswordResetLink = async (email: string) => {
  try {
    await userStore.postSendPasswordResetLink(email)
    activeAlert.value = true
    textAlert.value = 'Enlace de restablecimiento enviado correctamente'
    colorAlert.value = 'success'
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al enviar el enlace de restablecimiento'
    colorAlert.value = 'error'
  }
}

// ─── LIFECYCLE ─────────────────────────────────────────────────────────────

onMounted(async () => {
  await getUsers()
})

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader")!
  const _component_v_avatar = _resolveComponent("v-avatar")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_pagination = _resolveComponent("v-pagination")!
  const _component_v_data_table = _resolveComponent("v-data-table")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_sheet = _resolveComponent("v-sheet")!
  const _component_v_snackbar = _resolveComponent("v-snackbar")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_v_card, {
      elevation: "0",
      class: "bg-background pa-5 rounded-lg"
    }, {
      default: _withCtx(() => [
        (!activeCreateNewUser.value && !activeEditUser.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createVNode(_component_v_card, {
                class: "bg-background mt-3 rounded-lg",
                elevation: "3"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_2, [
                    _createVNode(_component_v_card_title, {
                      color: "primary",
                      class: "text-subtitle-4"
                    }, {
                      default: _withCtx(() => _cache[20] || (_cache[20] = [
                        _createTextVNode("Colaboradores ")
                      ])),
                      _: 1
                    }),
                    _createVNode(_component_v_spacer),
                    (!activeCreateNewUser.value && !activeEditUser.value)
                      ? (_openBlock(), _createBlock(_component_v_btn, {
                          key: 0,
                          onClick: btnSetActiveCreateUser,
                          variant: "text",
                          text: "Nuevo\n            colaborador "
                        }))
                      : (_openBlock(), _createBlock(_component_v_btn, {
                          key: 1,
                          onClick: _cache[0] || (_cache[0] = ($event: any) => ((activeCreateNewUser.value = false), (activeEditUser.value = false))),
                          variant: "text",
                          text: "Volver a los colaboradores"
                        }))
                  ]),
                  _createVNode(_component_v_text_field, {
                    modelValue: searchUsers.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchUsers).value = $event)),
                    "append-inner-icon": "mdi-magnify",
                    density: "compact",
                    label: "Buscar",
                    variant: "solo",
                    "hide-details": "",
                    "single-line": "",
                    color: "surface-light",
                    class: "mx-4",
                    "bg-color": "background"
                  }, null, 8, ["modelValue"]),
                  _createVNode(_component_v_data_table, {
                    class: "bg-background rounded-lg text-body-2",
                    page: page.value,
                    "onUpdate:page": _cache[3] || (_cache[3] = ($event: any) => ((page).value = $event)),
                    headers: headers,
                    items: users.value,
                    search: searchUsers.value,
                    loading: loading.value,
                    "items-per-page": itemsPerPage
                  }, {
                    loading: _withCtx(() => [
                      _createVNode(_component_v_skeleton_loader, {
                        class: "bg-background",
                        type: "table-row@10"
                      })
                    ]),
                    [`item.name`]: _withCtx(({ item }) => [
                      _createElementVNode("div", _hoisted_3, [
                        _createVNode(_component_v_avatar, {
                          size: "36",
                          class: "mr-3",
                          color: "primary"
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("span", _hoisted_4, _toDisplayString(item.name?.substring(0, 2).toUpperCase()), 1)
                          ]),
                          _: 2
                        }, 1024),
                        _createElementVNode("div", null, [
                          _createElementVNode("div", _hoisted_5, _toDisplayString(item.name), 1),
                          _createElementVNode("div", _hoisted_6, _toDisplayString(item.folderName), 1)
                        ])
                      ])
                    ]),
                    [`item.status`]: _withCtx(({ item }) => [
                      _createVNode(_component_v_chip, {
                        color: getStatusColor(item.role),
                        class: "text-caption",
                        size: "small",
                        variant: "tonal"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(getStatusLabel(item.role)), 1)
                        ]),
                        _: 2
                      }, 1032, ["color"])
                    ]),
                    [`item.actions`]: _withCtx(({ item }) => [
                      _createElementVNode("div", _hoisted_7, [
                        _withDirectives(_createVNode(_component_v_btn, {
                          variant: "text",
                          onClick: ($event: any) => (btnSetActiveEditUser(item)),
                          icon: "mdi-pencil-outline"
                        }, null, 8, ["onClick"]), [
                          [_directive_tooltip, `Editar`]
                        ]),
                        _withDirectives(_createVNode(_component_v_btn, {
                          variant: "text",
                          onClick: ($event: any) => (btnSendPasswordResetLink(item.email)),
                          icon: "mdi-lock-reset",
                          color: "orange"
                        }, null, 8, ["onClick"]), [
                          [_directive_tooltip, `Restablecer contraseña`]
                        ])
                      ])
                    ]),
                    bottom: _withCtx(() => [
                      _createElementVNode("div", _hoisted_8, [
                        _createVNode(_component_v_pagination, {
                          modelValue: page.value,
                          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((page).value = $event)),
                          length: pageCount.value
                        }, null, 8, ["modelValue", "length"])
                      ])
                    ]),
                    _: 2
                  }, 1032, ["page", "items", "search", "loading"])
                ]),
                _: 1
              })
            ]))
          : _createCommentVNode("", true),
        (activeCreateNewUser.value || activeEditUser.value)
          ? (_openBlock(), _createBlock(_component_v_row, {
              key: 1,
              justify: "center"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_col, {
                  cols: "12",
                  md: "10",
                  lg: "8"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", null, [
                      _createVNode(_component_v_card, {
                        elevation: "4",
                        class: "bg-background pa-0 ma-0 rounded-lg content-container"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("div", _hoisted_9, [
                            _createElementVNode("span", _hoisted_10, _toDisplayString(activeEditUser.value ? 'Editar colaborador' : 'Crear nuevo colaborador'), 1),
                            _createElementVNode("div", null, [
                              _createVNode(_component_v_btn, {
                                color: "red",
                                variant: "tonal",
                                class: "mr-2",
                                rounded: "lg",
                                onClick: resetForm
                              }, {
                                default: _withCtx(() => _cache[21] || (_cache[21] = [
                                  _createTextVNode("Limpiar")
                                ])),
                                _: 1
                              }),
                              _createVNode(_component_v_btn, {
                                color: "primary",
                                variant: "tonal",
                                class: "mr-2",
                                rounded: "lg",
                                onClick: _cache[4] || (_cache[4] = ($event: any) => (activeCreateNewUser.value = false, activeEditUser.value = false))
                              }, {
                                default: _withCtx(() => _cache[22] || (_cache[22] = [
                                  _createTextVNode("Volver")
                                ])),
                                _: 1
                              }),
                              (activeCreateNewUser.value)
                                ? (_openBlock(), _createBlock(_component_v_btn, {
                                    key: 0,
                                    color: "primary",
                                    class: "text-white",
                                    rounded: "lg",
                                    onClick: btnCreateUser,
                                    loading: loadingCreateUser.value
                                  }, {
                                    default: _withCtx(() => _cache[23] || (_cache[23] = [
                                      _createTextVNode(" Crear ")
                                    ])),
                                    _: 1
                                  }, 8, ["loading"]))
                                : _createCommentVNode("", true),
                              (activeEditUser.value)
                                ? (_openBlock(), _createBlock(_component_v_btn, {
                                    key: 1,
                                    color: "primary",
                                    class: "text-white",
                                    rounded: "lg",
                                    onClick: btnUpdateUser,
                                    loading: loadingEditUser.value
                                  }, {
                                    default: _withCtx(() => _cache[24] || (_cache[24] = [
                                      _createTextVNode(" Actualizar ")
                                    ])),
                                    _: 1
                                  }, 8, ["loading"]))
                                : _createCommentVNode("", true)
                            ])
                          ]),
                          _createVNode(_component_v_row, { justify: "center" }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_col, { cols: "12" }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_sheet, {
                                    color: "white",
                                    class: "pa-6",
                                    elevation: "0"
                                  }, {
                                    default: _withCtx(() => [
                                      _createVNode(_component_v_form, {
                                        ref_key: "formRef",
                                        ref: formRef,
                                        modelValue: formValid.value,
                                        "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((formValid).value = $event)),
                                        "validate-on": "submit lazy"
                                      }, {
                                        default: _withCtx(() => [
                                          _createElementVNode("div", _hoisted_11, [
                                            _cache[26] || (_cache[26] = _createElementVNode("h4", { class: "text-h6 mb-6" }, "1. Información del colaborador", -1)),
                                            _createVNode(_component_v_row, null, {
                                              default: _withCtx(() => [
                                                _createVNode(_component_v_col, {
                                                  cols: "12",
                                                  md: "6",
                                                  class: "mb-3"
                                                }, {
                                                  default: _withCtx(() => [
                                                    _createVNode(_component_v_text_field, {
                                                      modelValue: user.value.firstName,
                                                      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((user.value.firstName) = $event)),
                                                      label: "Nombre",
                                                      "error-messages": formTouched.value && !user.value.firstName ? ['Requerido'] : [],
                                                      variant: "outlined",
                                                      density: "comfortable",
                                                      "bg-color": "white"
                                                    }, null, 8, ["modelValue", "error-messages"])
                                                  ]),
                                                  _: 1
                                                }),
                                                _createVNode(_component_v_col, {
                                                  cols: "12",
                                                  md: "6",
                                                  class: "mb-3"
                                                }, {
                                                  default: _withCtx(() => [
                                                    _createVNode(_component_v_text_field, {
                                                      modelValue: user.value.lastName,
                                                      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((user.value.lastName) = $event)),
                                                      label: "Apellido",
                                                      "error-messages": formTouched.value && !user.value.lastName ? 'Requerido' : '',
                                                      variant: "outlined",
                                                      density: "comfortable",
                                                      "bg-color": "white"
                                                    }, null, 8, ["modelValue", "error-messages"])
                                                  ]),
                                                  _: 1
                                                }),
                                                _createVNode(_component_v_col, {
                                                  cols: "12",
                                                  md: "6",
                                                  class: "mb-3"
                                                }, {
                                                  default: _withCtx(() => [
                                                    _createVNode(_component_v_text_field, {
                                                      modelValue: user.value.email,
                                                      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((user.value.email) = $event)),
                                                      label: "Email",
                                                      "error-messages": formTouched.value && emailError.value ? [emailError.value] : [],
                                                      onBlur: _cache[8] || (_cache[8] = ($event: any) => (emailError.value = !user.value.email ? 'Email requerido' : (!_unref(validateEmail)(user.value.email) ? 'Email no válido' : ''))),
                                                      variant: "outlined",
                                                      density: "comfortable",
                                                      "bg-color": "white"
                                                    }, null, 8, ["modelValue", "error-messages"])
                                                  ]),
                                                  _: 1
                                                }),
                                                _createVNode(_component_v_col, {
                                                  cols: "12",
                                                  md: "6",
                                                  class: "mb-3"
                                                }, {
                                                  default: _withCtx(() => [
                                                    _createVNode(_component_v_select, {
                                                      modelValue: user.value.role,
                                                      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((user.value.role) = $event)),
                                                      label: "Rol *",
                                                      items: roleOptions,
                                                      "item-title": "name",
                                                      "item-value": "key",
                                                      "error-messages": formTouched.value && !user.value.role ? ['Rol requerido'] : [],
                                                      variant: "outlined",
                                                      density: "comfortable",
                                                      "bg-color": "white",
                                                      hint: "Seleccione el rol del colaborador",
                                                      "persistent-hint": ""
                                                    }, null, 8, ["modelValue", "error-messages"])
                                                  ]),
                                                  _: 1
                                                }),
                                                _createVNode(_component_v_col, {
                                                  cols: "12",
                                                  md: "6",
                                                  class: "mb-3"
                                                }, {
                                                  default: _withCtx(() => [
                                                    _createVNode(_component_v_text_field, {
                                                      modelValue: password.value,
                                                      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((password).value = $event)),
                                                      label: "Contraseña",
                                                      type: showPassword.value ? 'text' : 'password',
                                                      "append-icon": showPassword.value ? 'mdi-eye' : 'mdi-eye-off',
                                                      "onClick:append": _cache[11] || (_cache[11] = ($event: any) => (showPassword.value = !showPassword.value)),
                                                      "error-messages": formTouched.value && passwordError.value ? passwordError.value : '',
                                                      variant: "outlined",
                                                      density: "comfortable",
                                                      "bg-color": "white",
                                                      readonly: generateRandomPassword.value
                                                    }, null, 8, ["modelValue", "type", "append-icon", "error-messages", "readonly"])
                                                  ]),
                                                  _: 1
                                                }),
                                                _createVNode(_component_v_col, {
                                                  cols: "12",
                                                  md: "6",
                                                  class: "mb-3"
                                                }, {
                                                  default: _withCtx(() => [
                                                    _createVNode(_component_v_text_field, {
                                                      modelValue: confirmPassword.value,
                                                      "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((confirmPassword).value = $event)),
                                                      label: "Repetir contraseña",
                                                      type: showPassword.value ? 'text' : 'password',
                                                      "append-icon": showPassword.value ? 'mdi-eye' : 'mdi-eye-off',
                                                      "onClick:append": _cache[13] || (_cache[13] = ($event: any) => (showPassword.value = !showPassword.value)),
                                                      "error-messages": formTouched.value && passwordError.value ? passwordError.value : '',
                                                      variant: "outlined",
                                                      density: "comfortable",
                                                      "bg-color": "white",
                                                      readonly: generateRandomPassword.value
                                                    }, null, 8, ["modelValue", "type", "append-icon", "error-messages", "readonly"])
                                                  ]),
                                                  _: 1
                                                }),
                                                _createVNode(_component_v_col, {
                                                  cols: "12",
                                                  md: "6",
                                                  class: "mb-3"
                                                }, {
                                                  default: _withCtx(() => [
                                                    _createVNode(_component_v_checkbox, {
                                                      label: "Generar contraseña aleatoria",
                                                      modelValue: generateRandomPassword.value,
                                                      "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((generateRandomPassword).value = $event)),
                                                      "hide-details": "",
                                                      class: "mt-2 mb-4"
                                                    }, null, 8, ["modelValue"])
                                                  ]),
                                                  _: 1
                                                }),
                                                (generateRandomPassword.value && password.value)
                                                  ? (_openBlock(), _createBlock(_component_v_col, {
                                                      key: 0,
                                                      cols: "12",
                                                      md: "6",
                                                      class: "mb-3"
                                                    }, {
                                                      default: _withCtx(() => [
                                                        _createVNode(_component_v_btn, {
                                                          variant: "outlined",
                                                          onClick: copyToClipboard,
                                                          "prepend-icon": "mdi-content-copy",
                                                          class: "mt-2"
                                                        }, {
                                                          default: _withCtx(() => _cache[25] || (_cache[25] = [
                                                            _createTextVNode(" Copiar contraseña ")
                                                          ])),
                                                          _: 1
                                                        })
                                                      ]),
                                                      _: 1
                                                    }))
                                                  : _createCommentVNode("", true),
                                                _createVNode(_component_v_col, {
                                                  cols: "12",
                                                  md: "6",
                                                  class: "mb-3"
                                                }, {
                                                  default: _withCtx(() => [
                                                    _createVNode(_component_v_text_field, {
                                                      modelValue: user.value.folderName,
                                                      "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((user.value.folderName) = $event)),
                                                      label: "Nombre de carpeta *",
                                                      "error-messages": formTouched.value && !user.value.folderName ? ['Nombre de carpeta requerido'] : [],
                                                      "persistent-hint": ""
                                                    }, null, 8, ["modelValue", "error-messages"])
                                                  ]),
                                                  _: 1
                                                }),
                                                _createVNode(_component_v_col, {
                                                  cols: "12",
                                                  md: "6",
                                                  class: "mb-3"
                                                }, {
                                                  default: _withCtx(() => [
                                                    _createVNode(_component_v_checkbox, {
                                                      modelValue: sendEmailPassword.value,
                                                      "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((sendEmailPassword).value = $event)),
                                                      label: "Enviar contraseña por email",
                                                      class: "mt-2"
                                                    }, null, 8, ["modelValue"])
                                                  ]),
                                                  _: 1
                                                })
                                              ]),
                                              _: 1
                                            })
                                          ])
                                        ]),
                                        _: 1
                                      }, 8, ["modelValue"])
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(_component_v_snackbar, {
      modelValue: activeAlert.value,
      "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((activeAlert).value = $event)),
      timeout: 2000,
      color: colorAlert.value
    }, {
      actions: _withCtx(() => [
        _createVNode(_component_v_btn, {
          variant: "text",
          onClick: _cache[18] || (_cache[18] = ($event: any) => (activeAlert.value = false))
        }, {
          default: _withCtx(() => _cache[27] || (_cache[27] = [
            _createTextVNode(" Cerrar ")
          ])),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(textAlert.value) + " ", 1)
      ]),
      _: 1
    }, 8, ["modelValue", "color"])
  ], 64))
}
}

})